import ApiService from 'api-client/ApiService'
import { ServiceState } from 'app/plugins/infrastructure/components/common/service-health/constant'

export interface ImageMember {
  member_id: string
  image_id: string
  status: 'pending' | 'accepted' | 'rejected'
  created_at: string
  updated_at: string
  schema: string
}

export interface PendingImage {
  id: string
  name: string
  status: string
  protected: boolean
  disk_format: string
  virtual_size: number | null
  owner: string
}

class Glance extends ApiService {
  public getClassName() {
    return 'glance'
  }

  protected async getEndpoint() {
    return this.client.keystone.getServiceEndpoint('glance', 'public')
    // return this.client.keystone.getServiceEndpoint('glance', 'admin') // use this when using sbux-scaletest
  }

  // v2 = () => `/v2`

  imagesUrl = () => `/api/glance/v2/images`
  hostUrl = () => {
    return this.client.keystone.getServiceEndpoint('glance', 'admin')
  }

  getImages = async () => {
    const response = await this.client.basicGet<any>({
      url: `/v2/images?limit=1000`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getImages',
      },
    })
    return response.images
  }

  getImagesWithName = async (name) => {
    const response = await this.client.basicGet<any>({
      url: `/v2/images?name=in:"${name}"`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getImagesWithName',
      },
    })
    return response.images
  }

  async getImage(id) {
    const response = await this.client.basicGet<any>({
      url: `/v2/images/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getImage',
      },
    })
    return response
  }

  async createImage(body) {
    const projectId = this.client.activeProjectId
    const response = await this.client.basicPost<any>({
      url: `/v2/images?project_id=${projectId}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createImage',
      },
    })
    return response
  }

  async uploadImage(
    { id, body },
    options?: { onUploadProgress?: (progressEvent: any) => void; signal?: AbortController },
  ) {
    const hostUrl = await this.hostUrl()
    const config = options?.onUploadProgress
      ? {
          onUploadProgress: (progressEvent) => {
            options.onUploadProgress(progressEvent)
          },
        }
      : {}
    if (options?.signal) {
      config['signal'] = options.signal
    }
    try {
      const response = await this.client.basicPut<any>({
        endpoint: hostUrl,
        url: `/v2/images/${id}/file`,
        body,
        options: {
          clsName: this.getClassName(),
          mthdName: 'uploadImage',
          config: {
            headers: {
              'Content-Type': 'application/octet-stream',
            },
            timeout: 36000000, // 10 hours
            ...config,
          },
        },
      })
      return response
    } catch (error) {
      throw error
    }
  }

  async getHostGlanceEndpoint() {
    // Retrieve it each time, because a user may auth / deauth an image library and then the
    // keystone catalog changes
    // Also use the glance admin endpoint instead of glance-cluster bc glance-cluster can be multiple
    const endpoint = await this.client.keystone.getServiceEndpoint('glance', 'admin', true)
    return endpoint
  }

  async testHostGlanceAccess(endpoint?: string) {
    const hostUrl = endpoint ? endpoint : await this.getHostGlanceEndpoint()
    const response = await this.client.basicGet<any>({
      endpoint: hostUrl,
      url: '/v2/images?limit=1',
      options: {
        clsName: this.getClassName(),
        mthdName: 'testHostGlance',
        config: {
          timeout: 1000 * 15, // 15 seconds
        },
      },
    })
    return response
  }

  // ── Image import endpoints ──────────────────────────────────────────────────────────────────
  // POST /v2/images/{id}/import has TWO variants that MUST use DIFFERENT Glance endpoints. They are
  // deliberately two fully self-contained methods (no shared helper) so that a change to one can
  // NEVER alter the endpoint of the other:
  //   • importImage()        → DU public endpoint  (web-download / URL imports)
  //   • importImageOnHost()  → admin/host endpoint (copy-image, all_stores, any host-side import)
  // History: PCD-4317 repointed the single shared method to public and silently broke the
  // multi-store copy PCD-7717 shipped. Glance.test.ts now pins EACH method to its endpoint, so a
  // future repoint or a re-merge of these two fails the test instead of reaching production.
  // If you add a third import use case, pick the method by the ENDPOINT it needs, never rename.

  // web-download / URL imports ONLY → DU public endpoint (getEndpoint -> 'public').
  // Routed through the DU's glance-api pod, which delegates the download to the image-library host
  // via RPC; the admin/host endpoint would require direct host network access. Do NOT use for
  // copy-image / all_stores — that needs importImageOnHost() below.
  async importImage({ id, body }) {
    return this.client.basicPost<any>({
      url: `/v2/images/${id}/import`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'importImage',
      },
    })
  }

  // Generic image import against the admin/image-library HOST endpoint. Endpoint-descriptive, not
  // tied to one business op: today it backs the copy-image / all_stores fan-out (PCD-7717), and it
  // is the method to reuse for ANY future import that must run on the host with direct access to
  // every store. The caller supplies the body (e.g. { method: { name: 'copy-image' }, all_stores:
  // true }); this method only guarantees the request goes to the host endpoint, never the public.
  async importImageOnHost({ id, body }) {
    const hostUrl = await this.hostUrl()
    return this.client.basicPost<any>({
      endpoint: hostUrl,
      url: `/v2/images/${id}/import`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'importImageOnHost',
      },
    })
  }

  patchImage = async ({ id, body }) => {
    const response = await this.client.basicPatch<any>({
      url: `/v2/images/${id}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'patchImage',
        config: {
          headers: {
            'Content-Type': 'application/openstack-images-v2.1-json-patch',
          },
        },
      },
    })
    return response
  }

  deleteImage = async (id, deleteSnapshots = false) => {
    return this.client.basicDelete<any>({
      url: deleteSnapshots ? `/v2/images/${id}?delete_snapshots=true` : `/v2/images/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteImage',
      },
    })
  }

  // This function checks the health of the glance service by making a GET request to the glance endpoint
  getImageServiceHealth = async (endpoint: string) => {
    try {
      await this.client.basicGet<void>({
        endpoint,
        url: '/v2/images?limit=1',
        options: {
          clsName: this.getClassName(),
          mthdName: 'getGlanceServiceHealth',
        },
      })
      return { state: ServiceState.Up, message: 'Glance service is healthy' }
    } catch (error) {
      console.error('Error', error)
      // Incase of network error (Certificate not accepted),
      // OR if the user is note connected to VPN
      // return network_error

      if (error?.err?.message === 'Network Error') {
        return {
          state: ServiceState.CertificateError,
          message: 'Please trust the image library certificate.',
        }
      } else if (error?.err?.code === 'ECONNABORTED') {
        return { state: ServiceState.NetworkError, message: 'Glance image host not reachable' }
      }

      return { state: ServiceState.Down, message: error?.message || 'Unknown error' }
    }
  }

  listImageMembers = async ({ imageId }: { imageId: string }): Promise<ImageMember[]> => {
    const response = await this.client.basicGet<{ members: ImageMember[] }>({
      url: `/v2/images/${imageId}/members`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'listImageMembers',
      },
    })
    return response.members
  }

  addImageMember = async ({
    imageId,
    projectId,
  }: {
    imageId: string
    projectId: string
  }): Promise<ImageMember> => {
    const response = await this.client.basicPost<ImageMember>({
      url: `/v2/images/${imageId}/members`,
      body: { member: projectId },
      options: {
        clsName: this.getClassName(),
        mthdName: 'addImageMember',
      },
    })
    return response
  }

  updateImageMemberStatus = async ({
    imageId,
    memberId,
    status,
  }: {
    imageId: string
    memberId: string
    status: 'accepted' | 'rejected'
  }): Promise<ImageMember> => {
    const response = await this.client.basicPut<ImageMember>({
      url: `/v2/images/${imageId}/members/${memberId}`,
      body: { status },
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateImageMemberStatus',
      },
    })
    return response
  }

  removeImageMember = async ({
    imageId,
    memberId,
  }: {
    imageId: string
    memberId: string
  }): Promise<void> => {
    await this.client.basicDelete<void>({
      url: `/v2/images/${imageId}/members/${memberId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'removeImageMember',
      },
    })
  }

  getPendingSharedImages = async (): Promise<PendingImage[]> => {
    const response = await this.client.basicGet<{ images: PendingImage[] }>({
      url: `/v2/images?visibility=shared&member_status=pending`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getPendingSharedImages',
      },
    })
    return response.images
  }
}

export default Glance
